const AVATAR_HUES = [168, 186, 204, 142, 262, 32, 12, 220];

export function hashBrowseSeed(seed) {
  const s = String(seed || 'x');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export function getBrowseInitials(primary, secondary) {
  const a = String(primary || '').trim();
  const b = String(secondary || '').trim();
  if (a && b) return `${a[0]}${b[0]}`.toUpperCase();
  if (a.includes(' ')) {
    const parts = a.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  if (a.length >= 2) return a.slice(0, 2).toUpperCase();
  if (a.length === 1) return a.toUpperCase();
  return '—';
}

export function getBrowseAvatarStyle(seed) {
  const hue = AVATAR_HUES[hashBrowseSeed(seed) % AVATAR_HUES.length];
  return {
    '--recv-browse-avatar-hue': hue,
    '--recv-browse-avatar-bg': `hsla(${hue}, 58%, 42%, 0.14)`,
    '--recv-browse-avatar-fg': `hsl(${hue}, 52%, 28%)`,
    '--recv-browse-avatar-ring': `hsla(${hue}, 58%, 42%, 0.28)`
  };
}

export function memberDisplayName(member) {
  const nom = String(member?.nom || '').trim();
  const prenom = String(member?.prenom || '').trim();
  return [prenom, nom].filter(Boolean).join(' ') || '—';
}

export function formatMemberSexeShort(sexe) {
  const key = String(sexe || '').toUpperCase();
  if (key === 'M') return 'M';
  if (key === 'F') return 'F';
  return '—';
}

export function parseMemberBirthDate(dateNaissance) {
  if (!dateNaissance || String(dateNaissance).startsWith('0000')) return null;

  const raw = String(dateNaissance);
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(dateNaissance);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function computeMemberAgeYears(dateNaissance) {
  const birth = parseMemberBirthDate(dateNaissance);
  if (!birth) return null;

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

function computeMemberAgeMonthsAndDays(dateNaissance) {
  const birth = parseMemberBirthDate(dateNaissance);
  if (!birth) return null;

  const today = new Date();
  birth.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  if (today < birth) return null;

  let months =
    (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
  if (today.getDate() < birth.getDate()) months -= 1;

  const anchor = new Date(birth);
  anchor.setMonth(anchor.getMonth() + months);

  let days = Math.round((today - anchor) / 86400000);
  if (days < 0) {
    months -= 1;
    anchor.setMonth(anchor.getMonth() - 1);
    days = Math.round((today - anchor) / 86400000);
  }

  return { months: Math.max(0, months), days: Math.max(0, days) };
}

export function formatMemberAge(dateNaissance) {
  const years = computeMemberAgeYears(dateNaissance);
  if (years == null) return '—';

  if (years >= 3) return `${years} ans`;

  const diff = computeMemberAgeMonthsAndDays(dateNaissance);
  if (!diff) return '—';

  const { months, days } = diff;
  if (days <= 0) return `${months}M`;
  return `${months}M et ${days}j`;
}

/** Ligne meta recherche / liste (matricule, type, sexe, âge…). */
export function formatPersonProfileMeta({
  matricule,
  typeLabel,
  sexe,
  date_naissance,
  leading = []
} = {}) {
  const sex = formatMemberSexeShort(sexe);
  const age = formatMemberAge(date_naissance);
  const parts = [
    ...leading,
    matricule,
    typeLabel,
    sex !== '—' ? sex : null,
    age !== '—' ? age : null
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export function externalDisplayName(member) {
  return memberDisplayName({ nom: member?.nom, prenom: member?.prenom });
}
